import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const faqData = [
  {
    question: 'What is the relationship between Sai Enforcement And Security Pvt. Ltd and MHADA?',
    answer:
      'Sai Enforcement And Security Pvt. Ltd is an authorized agency appointed to facilitate specific MHADA housing projects. We act as a trusted bridge to guide applicants through the allocation and documentation process.',
  },
  {
    question: 'Is it safe to invest in these MHADA-approved projects?',
    answer:
      'Yes, these projects are government-approved, ensuring legal compliance, transparent pricing, and secure documentation, making them one of the safest investments in the market.',
  },
  {
    question: 'Do you offer loan assistance?',
    answer:
      'We provide comprehensive end-to-end support for bank loan applications, helping you secure up to 90% funding for your home with minimal documentation hurdles.',
  },
  {
    question: 'Are there any hidden costs?',
    answer:
      'We pride ourselves on 100% transparency. Every cost—including registration and legal fees—is clearly outlined before you proceed with any booking.',
  },
];

const PILLARS = [
  {
    title: 'Transparency',
    desc: 'Every cost, fee, and document explained upfront — no surprises.',
    color: BRAND.cyan,
    icon: <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />,
    extra: <circle cx="12" cy="12" r="3" />,
  },
  {
    title: 'Trust',
    desc: 'Government-approved MHADA projects with verified allocation.',
    color: BRAND.magenta,
    icon: (
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    ),
  },
  {
    title: 'Expertise',
    desc: 'End-to-end support from enquiry to possession and beyond.',
    color: BRAND.red,
    icon: (
      <>
        <path d="M16 7h6v6" />
        <path d="m22 7-8.5 8.5-5-5L2 17" />
      </>
    ),
  },
];

function PillarCard({ pillar, delay, visible }) {
  return (
    <div
      className={`about-reveal group ${visible ? 'about-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative h-full rounded-2xl p-[2px] ses-brand-gradient transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80">
        <div className="relative h-full rounded-[0.9rem] bg-white p-6 text-center">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
            style={{ background: `${pillar.color}14` }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={pillar.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {pillar.icon}
              {pillar.extra}
            </svg>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">{pillar.title}</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{pillar.desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  const [openIndex, setOpenIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={pageRef} className="w-full min-h-screen font-display overflow-x-hidden">
      {/* Page background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#EC008C]/6 via-[#00AEEF]/4 to-[#ED1C24]/5 pointer-events-none" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 px-4 md:px-10 overflow-hidden text-center">
        <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />
        <div className="absolute top-1/4 -left-24 w-72 h-72 rounded-full bg-[#00AEEF]/10 blur-[90px] pointer-events-none" />
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-[#EC008C]/8 blur-[100px] pointer-events-none" />

        <div className={`relative max-w-4xl mx-auto about-reveal ${visible ? 'about-visible' : ''}`}>
          <span className="mb-6 inline-flex items-center gap-2 rounded-full p-[1.5px] ses-brand-gradient shadow-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={BRAND.magenta} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              Who We Are
            </span>
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
            About{' '}
            <span className="ses-brand-text">Sai Enforcement And Security Pvt. Ltd</span>
          </h1>

          <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
            Bridging the gap between government-approved MHADA developments and aspiring
            homeowners with transparency, trust, and expertise.
          </p>

          <div className="mx-auto mt-8 h-1 w-24 rounded-full ses-brand-gradient opacity-80" />
        </div>
      </section>

      {/* Vision + pillars */}
      <section className="relative px-4 md:px-10 pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
            <div
              className={`about-reveal space-y-6 ${visible ? 'about-visible' : ''}`}
              style={{ transitionDelay: '150ms' }}
            >
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                Our Strategic{' '}
                <span className="ses-brand-text">Vision</span>
              </h2>
              <div className="h-1 w-24 rounded-full ses-brand-gradient" />
              <p className="text-slate-500 leading-relaxed text-lg">
                At Sai Enforcement And Security Pvt. Ltd., we believe in making home ownership
                accessible. As an authorized agency for MHADA, we simplify the complex real
                estate process, ensuring every client experiences a smooth, secure, and
                transparent journey from enquiry to possession.
              </p>
            </div>

            <div
              className={`about-reveal relative rounded-[1.75rem] p-[2px] ses-brand-gradient shadow-xl about-float-slow ${visible ? 'about-visible' : ''}`}
              style={{ transitionDelay: '250ms' }}
            >
              <div className="relative h-72 md:h-80 rounded-[1.65rem] bg-white overflow-hidden flex flex-col items-center justify-center p-8 text-center">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/8 via-white to-[#EC008C]/8" />
                <div className="relative flex -space-x-3 mb-6">
                  {[BRAND.cyan, BRAND.magenta, BRAND.red].map((c) => (
                    <div
                      key={c}
                      className="w-14 h-14 rounded-2xl border-4 border-white shadow-md flex items-center justify-center"
                      style={{ background: `${c}18` }}
                    >
                      <div className="w-4 h-4 rounded-full" style={{ background: c }} />
                    </div>
                  ))}
                </div>
                <p className="relative text-2xl font-black ses-brand-text uppercase tracking-wide">
                  Trusted Partnership
                </p>
                <p className="relative text-sm text-slate-500 mt-2 max-w-xs">
                  MHADA authorized · Shirdhon & Khoni-Kalyan projects
                </p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 mb-20">
            {PILLARS.map((pillar, i) => (
              <PillarCard key={pillar.title} pillar={pillar} delay={350 + i * 100} visible={visible} />
            ))}
          </div>

          {/* FAQ */}
          <div
            className={`about-reveal max-w-3xl mx-auto ${visible ? 'about-visible' : ''}`}
            style={{ transitionDelay: '450ms' }}
          >
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                Frequently Asked{' '}
                <span className="ses-brand-text">Questions</span>
              </h2>
              <p className="text-slate-500 mt-3">Everything you need to know before you book.</p>
            </div>

            <div className="space-y-3">
              {faqData.map((faq, index) => {
                const isOpen = openIndex === index;
                const accent = [BRAND.cyan, BRAND.magenta, BRAND.red, BRAND.cyan][index];

                return (
                  <div
                    key={index}
                    className={`rounded-2xl p-[2px] transition-all duration-300 ${
                      isOpen ? 'ses-brand-gradient shadow-md' : 'bg-slate-200/80'
                    }`}
                  >
                    <div className="rounded-[0.9rem] bg-white overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                        className="w-full flex justify-between items-start gap-4 p-5 md:p-6 text-left font-bold text-slate-900 hover:bg-slate-50/80 transition-colors"
                      >
                        <span className="text-sm md:text-base leading-snug">{faq.question}</span>
                        <span
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white transition-transform duration-300"
                          style={{
                            background: isOpen ? accent : `${accent}22`,
                            color: isOpen ? '#fff' : accent,
                            transform: isOpen ? 'rotate(45deg)' : 'none',
                          }}
                        >
                          +
                        </span>
                      </button>
                      <div
                        className={`grid transition-all duration-300 ease-out ${
                          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate-500 leading-relaxed text-sm md:text-base border-t border-slate-100 pt-4">
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-4 md:px-10 pb-20 lg:pb-28">
        <div
          className={`about-reveal max-w-4xl mx-auto rounded-3xl p-[2px] ses-brand-gradient shadow-xl ${visible ? 'about-visible' : ''}`}
          style={{ transitionDelay: '550ms' }}
        >
          <div className="rounded-[1.35rem] bg-white px-8 py-12 md:py-14 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Ready to find your{' '}
              <span className="ses-brand-text">home?</span>
            </h2>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              Explore our latest MHADA projects in Kalyan and take the first step toward ownership.
            </p>
            <Link
              to="/"
              className="relative inline-flex overflow-hidden group items-center gap-2 px-8 py-4 rounded-2xl text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,174,239,0.35)]"
            >
              <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="relative">View Projects</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="relative transition-transform group-hover:translate-x-1">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
